import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, useState} from 'react';

import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GREEN, RED} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import SecureTextInput from 'ui-library/inputs/SecureTextInput';
import TextField from 'ui-library/inputs/TextField';
import uniqueId from 'util/uniqueId';

import {
  ImportInstrumentDataModalAction,
  ImportInstrumentDataModalActionType,
} from './importInstrumentDataModalReducer';
import {
  ImportInstrumentDataModalState,
  InstrumentQuery,
  InstrumentQueryStatus,
} from './InstrumentDataConstants';
import {createValidateCredentialsInstrumentQuery, pollInstrumentQueryStatus} from './InstrumentDataDAO';

export interface InstrumentLoginPageProps {
  connectionId: string;
  dispatch: Dispatch<ImportInstrumentDataModalAction>;
  state: ImportInstrumentDataModalState;
  // Called with the username after the credential check returns DONE; not called on failure.
  onLoginSucceeded?: (username: string) => void;
}

interface LoginResult {
  status: InstrumentQueryStatus;
  // Why the check failed, when it failed for a reason other than the credentials being rejected.
  failureReason: string | null;
}

// The adapters' info for a login the instrument software rejected, as opposed to one it couldn't check.
const REJECTED_LOGIN_INFO_PREFIXES = ['Unauthorized', 'Forbidden'];

function failureReason(query: InstrumentQuery): string | null {
  if (query.status === InstrumentQueryStatus.DONE) {
    return null;
  }
  const info = query.info ?? '';
  return REJECTED_LOGIN_INFO_PREFIXES.some((prefix) => info.startsWith(prefix))
    ? null
    : info || 'An unknown error occurred';
}

// VALIDATE_CREDENTIALS only logs in, and the instruments service stores the entered password as the
// user's secret for the connection, so later commands need only the username.
function submitLogin(username: string, password: string, connectionId: string): Promise<LoginResult> {
  return createValidateCredentialsInstrumentQuery(connectionId, {username, password})
    .then(({instrumentQueryId}) => pollInstrumentQueryStatus(instrumentQueryId))
    .then((query): LoginResult => ({status: query.status, failureReason: failureReason(query)}))
    .catch((error: unknown): LoginResult => {
      // jQuery XHR rejections carry the server's message; polling rejects with an Error once it gives up.
      const serverMessage = (error as {responseJSON?: {userMessage?: string}})?.responseJSON?.userMessage;
      const message = serverMessage ?? (error instanceof Error ? error.message : null);
      return {status: InstrumentQueryStatus.ERROR, failureReason: message || 'An unknown error occurred'};
    });
}

export default function InstrumentLoginPage(props: InstrumentLoginPageProps): JSX.Element {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loginFailureReason, setLoginFailureReason] = useState<string | null>(null);

  return (
    <div>
      <Row>
        <Col sm={6}>
          <TextField
            name="Username"
            label="Username"
            labelStyle="label"
            isRequired={true}
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
            }}
            disabled={false}
          />
        </Col>
      </Row>
      <Row className={css(styles.rowRemoveMarginBottom)}>
        <Col sm={6}>
          <FieldLabel
            htmlFor={uniqueId('fieldLabel-')}
            isRequired={true}
            label="Password "
            labelStyle="label"
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <SecureTextInput name="Password" value={password} onChange={setPassword} disabled={false} />
        </Col>
        <Col>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-right"
            size={ButtonSize.SMALL}
            text={props.state.isLoggingIn ? 'Validating credentials...' : 'Validate credentials'}
            disabled={
              props.state.loginQueryStatus === InstrumentQueryStatus.DONE ||
              props.state.isLoggingIn ||
              !(username.length > 0 && password.length > 0)
            }
            onClick={async () => {
              props.dispatch({
                type: ImportInstrumentDataModalActionType.IS_LOGGING_IN,
                isLoggingIn: true,
              });
              const {status: loginQueryStatus, failureReason: reason} = await submitLogin(
                username,
                password,
                props.connectionId
              );
              setLoginFailureReason(reason);
              props.dispatch({
                type: ImportInstrumentDataModalActionType.LOGIN,
                loginQueryStatus,
                loginUsername: username,
              });
              if (loginQueryStatus === InstrumentQueryStatus.DONE) {
                props.onLoginSucceeded?.(username);
              }
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          {props.state.loginQueryStatus !== InstrumentQueryStatus.PENDING &&
            (props.state.loginQueryStatus === InstrumentQueryStatus.DONE ? (
              <div className={css(styles.validCredentials)}>Credentials entered successfully.</div>
            ) : (
              <div className={css(styles.invalidCredentials)}>
                {loginFailureReason == null
                  ? 'Invalid username or password.'
                  : `Couldn't validate credentials: ${loginFailureReason}`}
              </div>
            ))}
        </Col>
      </Row>
    </div>
  );
}

export const styles = StyleSheet.create({
  invalidCredentials: {
    color: RED,
    fontStyle: 'italic',
  },
  rowRemoveMarginBottom: {
    marginBottom: 0,
  },
  validCredentials: {
    color: GREEN,
    fontStyle: 'italic',
  },
});
